/**
 * System API service
 * Handles health checks, statistics, and system-related API requests
 */

import { get, post, put, del } from './api';
import type {
  HealthResponse,
  SystemStats,
  ProxyStatus,
  ProxiesResponse,
  AddProxyInput,
  ProxyTestResult,
} from '@/types/api';
import type { JobStats, ActivityResponse } from '@/types/job';

/**
 * API endpoints
 */
const ENDPOINTS = {
  HEALTH: '/health/detailed',
  STATS: '/stats',
  STATS_ACTIVITY: '/jobs/activity',
  STATS_JOBS: '/jobs/stats',
  PROXIES: '/proxies',
  PROXY: (id: string) => `/proxies/${id}`,
  PROXY_TEST: (id: string) => `/proxies/${id}/test`,
  SETTINGS: '/settings',
};

/**
 * Get system health status
 */
export async function getHealth(): Promise<HealthResponse> {
  return get<HealthResponse>(ENDPOINTS.HEALTH);
}

/**
 * Check if system is healthy
 */
export async function isSystemHealthy(): Promise<boolean> {
  try {
    const health = await getHealth();
    return health.status === 'healthy';
  } catch {
    return false;
  }
}

/**
 * Get system statistics
 */
export async function getSystemStats(): Promise<SystemStats> {
  return get<SystemStats>(ENDPOINTS.STATS);
}

/**
 * Get job statistics
 */
export async function getJobStats(): Promise<JobStats> {
  return get<JobStats>(ENDPOINTS.STATS_JOBS);
}

/**
 * Get activity data for charts
 */
export async function getActivityData(
  period: 'day' | 'week' | 'month' = 'week'
): Promise<ActivityResponse> {
  return get<ActivityResponse>(`${ENDPOINTS.STATS_ACTIVITY}?period=${period}`);
}

/**
 * Get dashboard statistics (combined)
 */
export async function getDashboardStats(): Promise<{
  health: HealthResponse;
  stats: SystemStats;
  jobStats: JobStats;
}> {
  const [health, stats, jobStats] = await Promise.all([
    getHealth(),
    getSystemStats(),
    getJobStats(),
  ]);

  return { health, stats, jobStats };
}

// ============ Proxy Management ============

/**
 * Get all proxies
 */
export async function getProxies(): Promise<ProxiesResponse> {
  return get<ProxiesResponse>(ENDPOINTS.PROXIES);
}

/**
 * Get a single proxy
 */
export async function getProxy(proxyId: string): Promise<ProxyStatus> {
  return get<ProxyStatus>(ENDPOINTS.PROXY(proxyId));
}

/**
 * Add a new proxy
 */
export async function addProxy(input: AddProxyInput): Promise<ProxyStatus> {
  return post<ProxyStatus, AddProxyInput>(ENDPOINTS.PROXIES, input);
}

/**
 * Delete a proxy
 */
export async function deleteProxy(proxyId: string): Promise<void> {
  return del<void>(ENDPOINTS.PROXY(proxyId));
}

/**
 * Test a proxy connection
 */
export async function testProxy(proxyId: string): Promise<ProxyTestResult> {
  return post<ProxyTestResult>(ENDPOINTS.PROXY_TEST(proxyId));
}

/**
 * Test a new proxy before adding
 */
export async function testNewProxy(input: AddProxyInput): Promise<ProxyTestResult> {
  return post<ProxyTestResult, AddProxyInput>(`${ENDPOINTS.PROXIES}/test`, input);
}

/**
 * Get healthy proxies count
 */
export async function getHealthyProxiesCount(): Promise<number> {
  const response = await getProxies();
  return response.active;
}

/**
 * Test all proxies
 */
export async function testAllProxies(): Promise<ProxyTestResult[]> {
  return post<ProxyTestResult[]>(`${ENDPOINTS.PROXIES}/test-all`);
}

/**
 * Refresh proxies from ProxyScrape API
 */
export async function refreshProxies(): Promise<{
  message: string;
  count: number;
  refreshedAt: string | null;
}> {
  return post(`${ENDPOINTS.PROXIES}/refresh`);
}

// ============ Proxy Type Configuration ============

/**
 * Proxy type configuration response
 */
export interface ProxyTypeConfig {
  proxyType: 'premium' | 'residential';
  residential: {
    host: string;
    port: number;
    isConfigured: boolean;
  };
  premium: {
    proxyCount: number;
    lastFetch: string | null;
  };
}

/**
 * Get current proxy type configuration
 */
export async function getProxyConfig(): Promise<ProxyTypeConfig> {
  return get<ProxyTypeConfig>(`${ENDPOINTS.PROXIES}/config`);
}

/**
 * Update proxy type
 */
export async function updateProxyType(proxyType: 'premium' | 'residential'): Promise<{
  message: string;
  proxyType: 'premium' | 'residential';
}> {
  return put(`${ENDPOINTS.PROXIES}/config`, { proxyType });
}

/**
 * Test residential proxy connection
 */
export async function testResidentialProxy(): Promise<{
  success: boolean;
  latency?: number;
  ip?: string;
  country?: string;
  isp?: string;
  error?: string;
}> {
  return post(`${ENDPOINTS.PROXIES}/residential/test`);
}

/**
 * Delete multiple proxies
 */
export async function deleteProxies(proxyIds: string[]): Promise<void> {
  await Promise.all(proxyIds.map((id) => deleteProxy(id)));
}

// ============ Settings ============

// Import ScraperSettings from types to ensure consistency with backend
import type { ScraperSettings } from '@/types/api';
export type { ScraperSettings };

/**
 * Get scraper settings
 */
export async function getScraperSettings(): Promise<ScraperSettings> {
  return get<ScraperSettings>(`${ENDPOINTS.SETTINGS}/scraper`);
}

/**
 * Update scraper settings
 */
export async function updateScraperSettings(
  settings: Partial<ScraperSettings>
): Promise<ScraperSettings> {
  return post<ScraperSettings, Partial<ScraperSettings>>(
    `${ENDPOINTS.SETTINGS}/scraper`,
    settings
  );
}

// ============ Queue Management ============

/**
 * Get queue statistics
 */
export async function getQueueStats(): Promise<{
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
}> {
  return get(`${ENDPOINTS.STATS}/queue`);
}

/**
 * Clear completed jobs from queue
 */
export async function clearCompletedQueue(): Promise<{ cleared: number }> {
  return post(`${ENDPOINTS.STATS}/queue/clear-completed`);
}

/**
 * Clear failed jobs from queue
 */
export async function clearFailedQueue(): Promise<{ cleared: number }> {
  return post(`${ENDPOINTS.STATS}/queue/clear-failed`);
}

// ============ Database Management ============

/**
 * Get database statistics
 */
export async function getDatabaseStats(): Promise<{
  jobs: number;
  places: number;
  gridCells: number;
  dbSizeBytes: number;
}> {
  return get(`${ENDPOINTS.STATS}/database`);
}

/**
 * Clear old data
 */
export async function clearOldData(
  olderThanDays: number
): Promise<{
  deletedJobs: number;
  deletedPlaces: number;
}> {
  return post(`${ENDPOINTS.SETTINGS}/clear-old-data`, { olderThanDays });
}

/**
 * Reset all data (dangerous!)
 */
export async function resetAllData(): Promise<void> {
  return post(`${ENDPOINTS.SETTINGS}/reset`);
}

// ============ Danger Zone Actions (Simplified: 3 robust actions) ============

/**
 * Stop All Jobs - Comprehensive job shutdown
 *
 * This is a robust, multi-step operation that:
 * 1. Signals worker to close all Puppeteer browser instances
 * 2. Pauses BullMQ queue to prevent new job pickups
 * 3. Drains all waiting/delayed jobs from BullMQ
 * 4. Clears cell execution locks from Redis
 * 5. Updates all running/pending jobs in DB to 'cancelled'
 * 6. Restarts the worker process fresh
 */
export async function stopAllJobs(): Promise<{
  message: string;
  details: {
    workerSignaled: boolean;
    queuePaused: boolean;
    bullmqJobsRemoved: number;
    cellLocksCleared: number;
    dbJobsCancelled: number;
    workerRestarted: boolean;
  };
}> {
  return post(`${ENDPOINTS.SETTINGS}/stop-all-jobs`);
}

/**
 * Reset Settings - Restore all settings to defaults
 */
export async function resetSettings(): Promise<{
  message: string;
  settings: Record<string, unknown>;
}> {
  return post(`${ENDPOINTS.SETTINGS}/reset-settings`);
}

/**
 * Factory Reset - Complete system wipe (Nuclear option)
 *
 * This is the most destructive operation:
 * 1. Signals worker to gracefully shutdown (closes browsers)
 * 2. Waits for browser cleanup
 * 3. Clears ALL Redis keys (queues, locks, cache)
 * 4. Truncates ALL database tables (jobs, places, cells, reviews)
 * 5. Resets settings to defaults
 * 6. Restarts worker with fresh state
 */
export async function factoryReset(): Promise<{
  message: string;
  details: {
    workerShutdown: boolean;
    redisKeysDeleted: number;
    jobsDeleted: number;
    placesDeleted: number;
    cellsDeleted: number;
    reviewsDeleted: number;
    proxyStatsDeleted: number;
    settingsReset: boolean;
    workerRestarted: boolean;
  };
}> {
  return post(`${ENDPOINTS.SETTINGS}/factory-reset`);
}

/**
 * Kill Chrome - Manual cleanup of orphaned Chrome processes
 *
 * Kills only Chrome processes belonging to this scraper (identified by SCRAPER_CHROME_ID).
 * Safe to use on shared servers - won't affect other Puppeteer scripts or user's browser.
 * Use this when you know no jobs are running and want to clean up orphaned browsers.
 */
export async function killChrome(): Promise<{
  message: string;
  details: {
    processesFound: number;
    processesKilled: number;
  };
}> {
  return post(`${ENDPOINTS.SETTINGS}/kill-chrome`);
}

/**
 * Get Chrome Process Count - Check for orphaned Chrome processes
 *
 * Returns the number of Chrome processes belonging to this scraper.
 * Useful to check before deciding whether to kill them.
 */
export async function getChromeCount(): Promise<{
  count: number;
  scraperId: string;
}> {
  return get(`${ENDPOINTS.SETTINGS}/chrome-count`);
}

// =============================================================================
// VISIBLE SEARCH TERMS
// =============================================================================

/**
 * Get search terms visible to non-admin users
 */
export async function getVisibleSearchTerms(): Promise<{ visibleTerms: string[] }> {
  return get(`${ENDPOINTS.SETTINGS}/visible-search-terms`);
}

/**
 * Set search terms visible to non-admin users
 */
export async function setVisibleSearchTerms(visibleTerms: string[]): Promise<{ visibleTerms: string[]; total: number }> {
  return put(`${ENDPOINTS.SETTINGS}/visible-search-terms`, { visibleTerms });
}

// =============================================================================
// CATEGORY CURATION (three-state: included / excluded / new)
// =============================================================================

export interface CategoryCurationEntry {
  category: string;
  status: 'included' | 'excluded';
}

/**
 * Get all curated categories with their status.
 * Categories not returned are "new" (unreviewed).
 */
export async function getCategoryCuration(): Promise<{ categories: CategoryCurationEntry[] }> {
  return get(`${ENDPOINTS.SETTINGS}/category-curation`);
}

/**
 * Set category curation states
 */
export async function setCategoryCuration(categories: CategoryCurationEntry[]): Promise<{ categories: CategoryCurationEntry[]; included: number; excluded: number; total: number }> {
  return put(`${ENDPOINTS.SETTINGS}/category-curation`, { categories });
}

/**
 * Get just the included category names (used by Results/Maps pages for the curated filter toggle)
 */
export async function getVisibleCategories(): Promise<{ visibleCategories: string[] }> {
  return get(`${ENDPOINTS.SETTINGS}/visible-categories`);
}

// =============================================================================
// NAME BLACKLIST (filter places by name pattern)
// =============================================================================

export interface NameBlacklistEntry {
  id: number;
  pattern: string;
  createdAt: string;
}

/**
 * Get all name blacklist entries (admin)
 */
export async function getNameBlacklist(): Promise<{ entries: NameBlacklistEntry[] }> {
  return get(`${ENDPOINTS.SETTINGS}/name-blacklist`);
}

/**
 * Add a name blacklist entry (admin)
 */
export async function addNameBlacklistEntry(pattern: string): Promise<NameBlacklistEntry> {
  return post(`${ENDPOINTS.SETTINGS}/name-blacklist`, { pattern });
}

/**
 * Remove a name blacklist entry by ID (admin)
 */
export async function removeNameBlacklistEntry(id: number): Promise<void> {
  return del(`${ENDPOINTS.SETTINGS}/name-blacklist/${id}`);
}

/**
 * Check if name blacklist has any entries (available to all users)
 */
export async function getNameBlacklistActive(): Promise<{ active: boolean }> {
  return get(`${ENDPOINTS.SETTINGS}/name-blacklist-active`);
}

